<template>
  <div
    v-if="tab"
    class="h-screen pb-safe flex flex-col pb-2"
    :class="{
      'overflow-hidden': showBillDiscounts || showBillsList || showProducts
    }"
  >
    <top-bar show-back :show-close="false" @back="router.back()">
      <template #center>
        <div class="flex items-center gap-2 font-heading font-bold text-xl">
          <span v-if="tab.code">
            <span class="font-bold">{{ tab.code }}</span>
            <span v-if="tab.tableName"> - {{ tab.tableName }}</span>
            <span v-else-if="tab.name"> - {{ tab.name }}</span>
          </span>
          <span v-else-if="tab.tableName">{{ tab.tableName }}</span>
          <span v-else>{{ tab.name }}</span>
        </div>
      </template>
    </top-bar>

    <div class="flex flex-col h-full p-4">
      <charge-calculator
        class="mb-4"
        :payment-methods="paymentMethods"
        :initial-pending="(selectedBill && selectedBill.pending) || 0"
        :show-discounts="true"
        keypad-mode="full"
        display-mode="pending"
        @change-payment-method="selectPaymentMethod"
        @calculator-change="calculatorChange"
        @show-discounts="showBillDiscounts = true"
      />

      <div class="w-full flex-none mb-2 flex gap-2">
        <l-button size="medium" color="blue" class="w-full" @click="showProducts = true">
          {{ t('checkout.view-products') }}
        </l-button>
        <l-button size="medium" class="w-full" :disabled="isCharging" @click="() => charge()">
          {{ t('checkout.charge') }}
          <span class="font-bold">
            {{ currency(calculatorValues.toPay) }}
          </span>
        </l-button>
      </div>
      <l-button
        size="medium"
        class="w-full flex-none"
        type="secondary"
        @click="showBillsList = true"
      >
        {{ t('checkout.view-payments') }}
      </l-button>
    </div>
  </div>
  <bill-discount
    v-if="showBillDiscounts"
    v-model:discount="discount"
    v-model:is-open="showBillDiscounts"
    :tab-id="tabId"
    :original-total="currentBill && currentBill.originalTotal"
    @close="showBillDiscounts = false"
    @cancel="showBillDiscounts = false"
  />
  <bills-list-modal
    v-if="showBillsList"
    :tab-id="tabId"
    :current-bill-id="currentBill && currentBill.id"
    :bills="bills"
    :selected-bill-id="selectedBillId"
    @close="showBillsList = false"
    @bill-selected="id => (selectedBillId = id)"
    @invalidate="invalidateAndRegenerate"
    @cancel-invoice="cancelInvoice"
    @generate-invoice="id => (generateInvoiceBillId = id)"
  />
  <teleport to="#appRoot">
    <div
      v-if="showProducts"
      class="fixed top-0 left-0 w-screen h-screen pb-safe flex flex-col bg-n-800"
    >
      <top-bar :show-close="true" @close="showProducts = false"></top-bar>

      <div class="flex w-full h-full overflow-hidden">
        <div class="flex flex-col shrink w-full">
          <checkout-tab
            :selected-product-ids="selectedProductsIds"
            class="flex-1"
            :tab-id="tabId"
            @toggle-product="toggleProduct"
          />
          <terrace-surcharge
            v-if="tableHasSurcharge"
            :tab-id="tabId"
            @toggle="onTerraceSurchargeToggled"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LButton, useDialog } from '@last/core-ui/paprika'

import type { CalculatorPayload } from '@/components/Calculator/types'
import ChargeCalculator from '@/components/ChargeCalculator/ChargeCalculator.vue'
import TopBar from '@/components/TopBarMobile.vue'
import { useCheckout } from '@/composables/useCheckout'
import { useMoney } from '@/composables/useMoney'
import { useTabs } from '@/composables/useTabs'
import BillsListModal from '@/mobile/BillsListModal.vue'
import BillDiscount from '@/mobile/CheckoutDiscount.vue'
import CheckoutTab from '@/modules/checkout/CheckoutTab.vue'
import TerraceSurcharge from '@/modules/checkout/TerraceSurcharge.vue'
import { useFloorplanStore } from '@/store/floorplan'
import { usePromotionsStore } from '@/store/promotions'
import { useTabsStore } from '@/store/tabs'
import type { Discount } from '@/types'

const route = useRoute()
const router = useRouter()

const tabId = route.params.tabId as string

const tabsStore = useTabsStore()
const promotionsStore = usePromotionsStore()
const floorplanStore = useFloorplanStore()
const { currency } = useMoney()
const dialog = useDialog()
const { t } = useI18n()

const showBillsList = ref<boolean>(false)
const showBillDiscounts = ref<boolean>(false)
const showProducts = ref<boolean>(false)

const currentDiscount = ref<Discount>()
const generateInvoiceBillId = ref<string>('')
const calculatorValues = ref<CalculatorPayload>({
  toPay: 0,
  pending: 0,
  change: 0,
  tip: 0
})

const { tab, closeTab, allProducts } = useTabs(() => tabId)
const {
  selectedProductsIds,
  selectedBillId,
  selectedBill,
  currentBill,
  bills,
  paymentMethods,
  isCharging,
  charge,
  selectPaymentMethod,
  checkoutCompleteWithPayments
} = useCheckout(tabId, 'normalCheckout', calculatorValues)

const { removeBill, regenerateBill, updateTerraceSurchargePercentage } = tabsStore
const { getTabGlobalPromotion } = storeToRefs(promotionsStore)
const { tables } = storeToRefs(floorplanStore)

onMounted(() => {
  if (tabGlobalPromotion.value) {
    refreshGlobalPromotion()
  }
  if (bills.value && bills.value.length > 0) {
    selectedBillId.value = bills.value[0].id
  }
})

const tabGlobalPromotion = computed(() => {
  return getTabGlobalPromotion.value(tabId)
})

function refreshGlobalPromotion(): void {
  if (tabGlobalPromotion.value) {
    currentDiscount.value = {
      type: tabGlobalPromotion.value.discountType,
      amount: tabGlobalPromotion.value.discountAmount,
      promotionId: tabGlobalPromotion.value.promotionId || '',
      concept: tabGlobalPromotion.value.description
    }
  } else {
    currentDiscount.value = {
      type: 'percentage',
      amount: 0,
      concept: ''
    }
  }
}

const tableHasSurcharge = computed(() => {
  if (!tab || !tab.value) return false
  const table = tables.value[tab.value.tables[0]]

  return (
    (table && table.terraceSurchargePercentage > 0) ||
    (tab.value.terraceSurchargePercentage && tab.value.terraceSurchargePercentage > 0)
  )
})

function calculatorChange(value: CalculatorPayload) {
  calculatorValues.value = value
}

watch(checkoutCompleteWithPayments, complete => {
  const tabProducts = allProducts.value
  if (complete && tabProducts.length > 0) {
    closeTab()
  }
})

function deleteCurrentTabPromotion() {
  if (tabGlobalPromotion.value) {
    promotionsStore.deleteTabPromotion({
      tabId: tabId,
      tabPromotion: tabGlobalPromotion.value
    })
  }
}

const discount = computed({
  get() {
    return currentDiscount.value
  },
  set(discount) {
    if (!discount || !discount.type) {
      deleteCurrentTabPromotion()
      currentDiscount.value = undefined
    } else {
      promotionsStore
        .updateTabPromotion({
          tabId: tabId,
          promotion: {
            discountType: discount.type,
            discountAmount: discount.amount,
            name: discount.name,
            id: discount.promotionId,
            global: discount.global,
            description: discount.concept
          }
        })
        .then(() => {
          currentDiscount.value = discount
          refreshGlobalPromotion()
        })
    }
  }
})

function invalidateAndRegenerate(billId: string) {
  dialog({
    title: t('checkout.invalidate-bill'),
    content: t('checkout.invalidate-bill-content'),
    mainLabel: t('ctas.yes'),
    onConfirm: () => {
      removeBill({ tabId: tabId, billId })
      if (currentBill.value) {
        selectedBillId.value = currentBill.value.id
      } else if (bills.value && bills.value.length > 0) {
        selectedBillId.value = bills.value[0].id
      }
    }
  })
}

function toggleProduct(productId: { id: string; uniqueId: string }): void {
  if (selectedProductsIds.value.map(id => id.uniqueId).includes(productId.uniqueId)) {
    selectedProductsIds.value = selectedProductsIds.value.filter(
      id => id.uniqueId != productId.uniqueId
    )
  } else {
    selectedProductsIds.value = [...selectedProductsIds.value, productId]
  }
  selectedBillId.value = currentBill.value.id
}

function cancelInvoice(billId: string) {
  selectedBillId.value = regenerateBill({
    tabId: tabId,
    billId: billId,
    customerCompany: undefined
  })
}

function onTerraceSurchargeToggled(newValue: boolean): void {
  updateTerraceSurchargePercentage({
    tabId: tabId,
    enabled: newValue
  })
}
</script>
